import React, { useEffect, useState } from 'react';
import ManageNotifications from './ManageNotifications';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Avatar } from 'primereact/avatar';
import './ManageNotifications.container.css';
import { Button } from 'primereact/button';
import { showToast } from '../../helpers/showToast';
import { useTranslation } from 'react-i18next';
import * as api from '../../services/apis';
import { useAuthentication } from '../../hooks/useAuthentication';
import lodash from 'lodash';

const ManageNotificationsContainer = (props) => {
  const { t } = useTranslation();

  const { userInfo } = useAuthentication();
  const [pointsOfContact, setPointsOfContact] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [updatedContacts, setUpdatedContacts] = useState([]);
  const [prefsUpdated, setPrefsUpdated] = useState(false);

  const onPointOfContactAdd = async ({ email, phone }) => {
    setLoading(true);
    api
      .addPointOfContact(
        {
          email,
          phone,
          taxpayerId: userInfo.userId,
          isPrimary: false,
        },
        userInfo.tax_number,
      )
      .then(() => {
        showToast({
          data: {
            variant: 'success',
            text: t('manage-notifications.confirmation-link-sent', { email }),
          },
        });
        setAddingContact(false);
        getPointsOfContact();
      })
      .finally(() => {
        setLoading(false);
      });

    return true;
  };

  const toggleEmail = (i) => {
    let tempPointsOfContact = lodash.cloneDeep(updatedContacts);
    tempPointsOfContact[i].channelMail = !tempPointsOfContact[i].channelMail;
    setPrefsUpdated(true);
    setUpdatedContacts(tempPointsOfContact);
  };

  const togglePhoneNumber = (i) => {
    let tempPointsOfContact = lodash.cloneDeep(updatedContacts);
    tempPointsOfContact[i].channelSms = !tempPointsOfContact[i].channelSms;
    setPrefsUpdated(true);
    setUpdatedContacts(tempPointsOfContact);
  };

  const saveToggle = () => {
    setLoading(true);

    const contacts = lodash.differenceWith(
      updatedContacts,
      pointsOfContact,
      lodash.isEqual,
    );

    Promise.all(
      contacts.map((contact) =>
        api.updatePointOfContact(
          {
            contactId: contact.contactId,
            emailEnabled: contact.channelMail,
            smsEnabled: contact.channelSms,
          },
          userInfo.tax_number,
        ),
      ),
    )
      .then((e) => {
        showToast({
          data: {
            variant: 'success',
            text: 'Changes have been updated successfully',
          },
        });
        getPointsOfContact();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onPointOfContactRemove = (pointOfContact, i) => {
    showRemoveDialog(
      t('manage-notifications.remove-point-of-contact'),
      t(
        'manage-notifications.are-you-sure-you-want-to-remove-point-of-contact-mentioned-below',
      ),
      pointOfContact.contactValueMail,
      pointOfContact.contactValueSms,
      async () => {
        setLoading(true);
        api
          .deletePointOfContact(pointOfContact.contactId, userInfo.tax_number)
          .then(() => {
            getPointsOfContact();
            showToast({
              data: {
                variant: 'success',
                text: t('manage-notifications.contact-deleted-successfully'),
              },
            });
          })
          .finally(() => {
            setLoading(false);
          });
      },
    );
  };

  const showRemoveDialog = (title, message, email, phone, onAccept) =>
    confirmDialog({
      group: 'removeDialog',
      className: 'manage-notifications-removeDialog',
      header: (
        <div className="header">
          <Avatar icon="pi pi-trash" shape="circle" size="xlarge" />
          <h5>{title}</h5>
        </div>
      ),
      message: (
        <div className="content">
          <span>{message}</span>
          <ul>
            <li>{email}</li>
            <li>{phone}</li>
          </ul>
        </div>
      ),
      footer: ({ accept, reject }) => (
        <>
          <Button outlined label={t('common.cancel')} onClick={reject} />
          <Button label={t('manage-notifications.confirm')} onClick={accept} />
        </>
      ),
      accept: onAccept,
    });

  const getPointsOfContact = async () => {
    setLoading(true);
    api
      .getPointsOfContact(userInfo.userId, userInfo.tax_number)
      .then((response) => {
        setPointsOfContact(response?.data ?? []);
        setUpdatedContacts(lodash.cloneDeep(response?.data) ?? []);
        setPrefsUpdated(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getPointsOfContact();
  }, []);

  return (
    <>
      <ManageNotifications
        {...props}
        pointsOfContact={pointsOfContact}
        onPointOfContactAdd={onPointOfContactAdd}
        toggleEmail={toggleEmail}
        togglePhoneNumber={togglePhoneNumber}
        onPointOfContactRemove={onPointOfContactRemove}
        loading={loading}
        setAddingContact={setAddingContact}
        addingContact={addingContact}
        setUpdatedContacts={setUpdatedContacts}
        updatedContacts={updatedContacts}
        onSave={saveToggle}
        prefsUpdated={prefsUpdated}
      />
      <ConfirmDialog group="removeDialog" />
    </>
  );
};

export default React.memo(ManageNotificationsContainer);
